/**
 * QRScanner Component (Story 7.3)
 *
 * Camera-based QR code scanner for coupon redemption.
 * Uses @yudiel/react-qr-scanner for device camera access.
 *
 * Features:
 * - Full-screen scanner with viewfinder overlay
 * - Camera flip (front/back)
 * - Visual feedback on successful scan
 * - Error states for camera permission issues
 * - Accessibility support
 *
 * Supported QR formats:
 * - ZLINKX:{userCouponId}:{checksum}
 * - Plain coupon code (4 chars: [0-9][A-Z][0-9][A-Z])
 * - Legacy formats (UC123, 6010)
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Scanner } from '@yudiel/react-qr-scanner';

// Type for detected barcode from scanner
interface DetectedBarcode {
	rawValue: string;
	format?: string;
}
import { CameraOff, RefreshCw, X, FlipHorizontal2 } from 'lucide-react';
import { useTranslation } from '@/i18n';

interface QRScannerProps {
	onScanSuccess: (code: string) => void;
	onScanError?: (error: string) => void;
	onClose: () => void;
	isProcessing?: boolean;
}

const QRScanner: React.FC<QRScannerProps> = ({
	onScanSuccess,
	onScanError,
	onClose,
	isProcessing = false,
}) => {
	const { t } = useTranslation();
	const [error, setError] = useState<string | null>(null);
	const [facingMode, setFacingMode] = useState<'environment' | 'user'>('environment');
	const lastScannedRef = useRef<string | null>(null);
	const scanLockRef = useRef(false);

	// Reset scan lock after processing completes
	useEffect(() => {
		if (!isProcessing) {
			scanLockRef.current = false;
		}
	}, [isProcessing]);

	const handleScan = useCallback(
		(detectedCodes: DetectedBarcode[]) => {
			// Prevent multiple scans while processing
			if (scanLockRef.current || isProcessing) return;

			if (detectedCodes.length > 0) {
				const code = detectedCodes[0]?.rawValue;
				if (code && code !== lastScannedRef.current) {
					lastScannedRef.current = code;
					scanLockRef.current = true;
					onScanSuccess(code);
				}
			}
		},
		[onScanSuccess, isProcessing]
	);

	const handleError = useCallback(
		(err: unknown) => {
			console.error('QR Scanner error:', err);
			const error = err as Error;
			let errorMessage: string;

			if (error.name === 'NotAllowedError') {
				errorMessage = t('merchant.scan.errors.cameraPermissionDenied');
			} else if (error.name === 'NotFoundError') {
				errorMessage = t('merchant.scan.errors.noCameraFound');
			} else {
				errorMessage = t('merchant.scan.errors.cameraError');
			}

			setError(errorMessage);
			onScanError?.(errorMessage);
		},
		[t, onScanError]
	);

	const toggleCamera = useCallback(() => {
		setFacingMode(prev => (prev === 'environment' ? 'user' : 'environment'));
	}, []);

	const retryCamera = useCallback(() => {
		setError(null);
		lastScannedRef.current = null;
		scanLockRef.current = false;
	}, []);

	return (
		<div className='fixed inset-0 z-50 bg-black'>
			{/* Scanner View */}
			<div className='relative w-full h-full'>
				{error ? (
					/* Error State */
					<div className='absolute inset-0 flex flex-col items-center justify-center bg-gray-900 text-white p-6'>
						<CameraOff className='w-16 h-16 text-red-400 mb-4' />
						<h3 className='text-xl font-semibold mb-2'>
							{t('merchant.scan.errors.title')}
						</h3>
						<p className='text-gray-400 text-center mb-6 max-w-sm'>{error}</p>
						<div className='flex gap-4'>
							<button
								onClick={retryCamera}
								className='flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors'
							>
								<RefreshCw className='w-5 h-5' />
								{t('merchant.scan.retry')}
							</button>
							<button
								onClick={onClose}
								className='flex items-center gap-2 px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors'
							>
								{t('merchant.scan.enterManually')}
							</button>
						</div>
					</div>
				) : (
					<>
						{/* QR Scanner */}
						<Scanner
							onScan={handleScan}
							onError={handleError}
							constraints={{
								facingMode: facingMode,
							}}
								styles={{
									container: {
										width: '100%',
										height: '100%',
									},
								video: {
									width: '100%',
									height: '100%',
										objectFit: 'cover',
									},
								}}
								components={{
									torch: false,
								}}
								scanDelay={300}
							/>

						{/* Viewfinder Overlay */}
						<div className='absolute inset-0 pointer-events-none'>
							{/* Semi-transparent background */}
							<div className='absolute inset-0 bg-black/40' />

							{/* Clear scanning area */}
							<div className='absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 sm:w-80 sm:h-80'>
								{/* Clear center - cut out from overlay */}
								<div
									className='absolute inset-0 bg-transparent border-2 border-white/80 rounded-lg'
									style={{
										boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.4)',
									}}
								>
									{/* Corner accents */}
									<div className='absolute -top-0.5 -left-0.5 w-8 h-8 border-t-4 border-l-4 border-blue-500 rounded-tl-lg' />
									<div className='absolute -top-0.5 -right-0.5 w-8 h-8 border-t-4 border-r-4 border-blue-500 rounded-tr-lg' />
									<div className='absolute -bottom-0.5 -left-0.5 w-8 h-8 border-b-4 border-l-4 border-blue-500 rounded-bl-lg' />
									<div className='absolute -bottom-0.5 -right-0.5 w-8 h-8 border-b-4 border-r-4 border-blue-500 rounded-br-lg' />
								</div>
							</div>

							{/* Instructions text */}
							<div className='absolute bottom-32 left-0 right-0 text-center'>
								<p className='text-white text-lg font-medium'>
									{isProcessing
										? t('merchant.scan.processing')
										: t('merchant.scan.instructions')}
								</p>
							</div>
						</div>
					</>
				)}
			</div>

			{/* Control Bar */}
			<div className='absolute top-0 left-0 right-0 flex items-center justify-between p-4 bg-gradient-to-b from-black/60 to-transparent'>
				<button
					onClick={onClose}
					className='p-3 bg-black/40 rounded-full text-white hover:bg-black/60 transition-colors'
					aria-label={t('common.cancel')}
				>
					<X className='w-6 h-6' />
				</button>

				<h2 className='text-white text-lg font-semibold'>{t('merchant.scan.title')}</h2>

				{!error && (
					<button
						onClick={toggleCamera}
						className='p-3 bg-black/40 rounded-full text-white hover:bg-black/60 transition-colors'
						aria-label={t('merchant.scan.flipCamera')}
					>
						<FlipHorizontal2 className='w-6 h-6' />
					</button>
				)}
				{error && <div className='w-12' />}
			</div>

			{/* Processing Overlay */}
			{isProcessing && (
				<div className='absolute inset-0 bg-black/60 flex items-center justify-center'>
					<div className='bg-white rounded-xl p-6 text-center'>
						<div className='w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4' />
						<p className='text-gray-900 font-medium'>{t('merchant.scan.validating')}</p>
					</div>
				</div>
			)}
		</div>
	);
};

export default QRScanner;
